// src/services/receptionist/types.ts
//
// Wire contract for the WhatsApp AI Receptionist console API —
// ourteam-frontend#3606, BE epic ourteam-backend#4666. The payloads are
// serialised STRAIGHT into the interfaces the console was built against, in
// camelCase, so most of this file is a re-export from @receptionist/*: those
// files are the contract, and a second copy here is how the two would drift.
//
// Only the shapes that have no home in the product model are declared here:
// the WhatsApp onboarding state (#4671), the staff roll-up row (#4670), the
// request parameters, and the result unions that carry an EXPECTED non-2xx —
// a 404 that means "not yours", a 409 that means "someone got there first",
// a 422 that carries the validator's own words. Those are outcomes the screen
// has to tell apart, not exceptions.

import type { MetaPagination } from "@type/common";
import type { MerchantConfig, MerchantCategory } from "@receptionist/merchant";
import type { BookingFlow } from "@receptionist/flow";
import type { Booking } from "@receptionist/bookings";
import type { Proposal } from "@receptionist/transcripts";

export type {
    MerchantConfig, MerchantCategory, Tone, Service, OpeningHours, StaffMember, EscalationRule,
} from "@receptionist/merchant";
export type {
    BookingFlow, BookingFlowStep, BookingStepType, BookingAdvance, BookingGate,
    AnswerKnockoutGate, DeadlineGate, BookingQuestion, Opener, FollowUp, FollowUpTrigger,
    StopRule, TemplateStatus, KnockoutOperator,
} from "@receptionist/flow";
export type { Booking, BookingStatus, Turn, TurnAuthor } from "@receptionist/bookings";
export type { Proposal, ProposalKind, TranscriptFile } from "@receptionist/transcripts";
export type { MetaPagination };

// ─── Shared ──────────────────────────────────────────────────────────────────

/** A request that did not reach a 2xx and that the caller cannot act on. */
export interface RequestFailure {
    ok: false;
    kind: "error";
    /** Human-readable, from the server when it said something and a fallback
     *  when it did not. Safe to show. */
    message: string;
}

// ─── Merchant config (#4667) ─────────────────────────────────────────────────

/**
 * `GET .../merchants/{merchantId}`.
 *
 * ONLY a 404 means "not yours or not there" — the backend answers 404 for
 * both on purpose, so the two are not distinguishable here either. Anything
 * else is a failure to ask, and the screen must not treat it as absence.
 */
export type MerchantResult =
    | { ok: true; merchant: MerchantConfig }
    | { ok: false; kind: "notFound" }
    | RequestFailure;

/**
 * What the console may SEND on `PUT .../merchants/{merchantId}`.
 *
 * The read-only fields — `id`, `whatsappConnected`, `approvedTemplates`, and
 * `staffChannel.connected` / `memberCount` — are owned by the WhatsApp
 * onboarding (#4671) and the staff-channel connection, not by the form. The
 * server ignores them if sent; excluding them at the type level means no
 * caller can offer an input for them by mistake. `toMerchantInput` strips a
 * full config down to this.
 */
export type MerchantConfigInput = Omit<MerchantConfig, "id" | "whatsappConnected" | "approvedTemplates" | "staffChannel"> & {
    staffChannel: { name: string };
};

export function toMerchantInput(config: MerchantConfig): MerchantConfigInput {
    const {
        id: _id, whatsappConnected: _c, approvedTemplates: _t, staffChannel, ...rest
    } = config;
    void _id; void _c; void _t;
    // A service's image is the image endpoints' to set: left out, the server keeps it.
    const services = rest.services.map(({ imageKey: _k, imageUrl: _u, ...s }) => { void _k; void _u; return s; });
    return { ...rest, services, staffChannel: { name: staffChannel.name } };
}

/** `POST|DELETE .../services/{serviceId}/image`: the merchant as it now is, or why not. */
export type ServiceImageResult =
    | { ok: true; merchant: MerchantConfig }
    | { ok: false; message: string };

/** A count this week and the same count the week before. */
export interface WeekCount {
    count: number;
    previous: number;
}

/** `GET .../merchants/{merchantId}/overview` (ourteam-backend#6718): the console's landing page. */
export interface ReceptionistOverview {
    windowDays: number;
    since: string;
    /** The whole Pending queue, and when its oldest request arrived. */
    pending: { count: number; oldestAt: string | null };
    bookings: WeekCount & { confirmed: number; declined: number; afterHours: number };
    /** WhatsApp conversations with a customer message; simulated ones are left out. */
    conversations: WeekCount & { aiReplies: number };
    handedToTeam: WeekCount;
    followUpsSent: number;
    nudged: number;
    /** Confirmed appointments on the merchant's today, earliest first. */
    today: { id: string; customer: string; service: string; slotAt: string | null }[];
}

/**
 * `PUT .../merchants/{merchantId}`.
 *
 * `invalid` carries the server's own sentence, e.g. a locked escalation rule
 * switched off, or a half-set opening-hours row. `code` is the machine value
 * from `errors[0]` when the server sent one (`escalation_rule_locked`).
 */
export type SaveMerchantResult =
    | { ok: true; merchant: MerchantConfig }
    | { ok: false; kind: "invalid"; message: string; code?: string }
    | RequestFailure;

// ─── Booking flow (#4668) ────────────────────────────────────────────────────

/**
 * `PUT .../merchants/{merchantId}/flow` answers 422 with the validator's
 * problems — the same twelve rules as the FE's `validateFlow`, in the same
 * words — and that is an expected outcome, not an exception. The screen
 * renders `problems` in the same list as its own.
 */
export type SaveFlowResult =
    | { ok: true; flow: BookingFlow }
    | { ok: false; kind: "invalid"; problems: string[] }
    | RequestFailure;

/**
 * What the console may SEND on `PUT .../merchants/{merchantId}/flow`.
 *
 * `templateStatus` on a follow-up is Meta's approval state and belongs to the
 * template registry (#4671); the server ignores a client value and re-derives
 * it on every read. Stripping it here means no screen can ever send one, and
 * a follow-up's status on screen is always the server's last word.
 */
export type BookingFlowInput = Omit<BookingFlow, "followUps"> & {
    followUps: Omit<BookingFlow["followUps"][number], "templateStatus">[];
};

export function toFlowInput(flow: BookingFlow): BookingFlowInput {
    return {
        ...flow,
        followUps: flow.followUps.map(({ templateStatus: _ts, ...f }) => { void _ts; return f; }),
    };
}

/** One of the two starter flows from `GET /flow-templates`. */
export interface FlowTemplate {
    key: string;
    label: string;
    description: string;
    flow: BookingFlow;
}

// ─── Bookings (#4669) ────────────────────────────────────────────────────────

export type BookingsFilter = "all" | "pending" | "confirmed" | "closed";

export interface ListBookingsParams {
    /** `closed` means Declined OR Cancelled. Default `all`. */
    status?: BookingsFilter;
    /** 1-based. Default 1. */
    page?: number;
    /** Max 100. Default 50. */
    limit?: number;
}

export interface BookingsPage {
    /** Counts for the WHOLE queue, not the returned page, so the tab counts
     *  stay right while paginated. */
    counts: Record<BookingsFilter, number>;
    pagination: MetaPagination;
    bookings: Booking[];
}

/**
 * `POST .../bookings/{bookingId}/confirm` and `/decline` answer 409 with two
 * DIFFERENT codes the screen must treat differently: `not_pending` means the
 * world moved on (someone confirmed in the staff group), so the screen
 * refetches; `template_not_approved` means the customer could not be messaged
 * at all, the booking is untouched, and the screen refetches nothing.
 * `message` is the server's, verbatim.
 */
export type BookingDecisionResult =
    | { ok: true; booking: Booking }
    | { ok: false; kind: "not_pending"; message: string }
    | { ok: false; kind: "template_not_approved"; message: string }
    | RequestFailure;

/**
 * Sending a free-form message on a booking thread.
 *
 * Two shapes go to the same endpoint: `{ text }` is a free-form reply, which
 * WhatsApp only delivers inside the 24-hour window, and `{ template }` is one
 * of the merchant's approved templates, which is the only thing that still
 * delivers once that window has shut.
 *
 * PROPOSED CONTRACT — no BE ticket defines this yet (ourteam-backend#4669
 * covers confirm/decline only). Shaped like BookingDecisionResult so the
 * screens read the same, and `window_closed` mirrors the 24-hour rule the
 * candidate composer already meets (#3017). Needs a BE ticket before the live
 * API flag goes on; until then it only runs against the fixtures.
 */
export type BookingMessageInput = { text: string } | { template: WhatsappTemplateKey };

export type SendBookingMessageResult =
    | { ok: true; booking: Booking }
    | { ok: false; kind: "window_closed"; message: string }
    | RequestFailure;

// ─── Staff roll-up (#4670) ───────────────────────────────────────────────────

/**
 * Derived server-side: the first checklist item not yet done, in this order,
 * or `live` when all are. Never recomputed on the client.
 */
export type OnboardingStage =
    | "details"
    | "meta_verification"
    | "number_migration"
    | "templates_pending"
    | "kb_signoff"
    | "live";

/** One row of `GET /merchants`. */
export interface MerchantSummary {
    id: string;
    name: string;
    category: MerchantCategory;
    outlet: string;
    whatsappConnected: boolean;
    onboardingStage: OnboardingStage;
    pendingBookings: number;
    bookingsLast7Days: number;
    escalationsLast7Days: number;
    /** Last INBOUND customer message; the AI's own sends never move it.
     *  `null` for a merchant that has never received one. */
    lastActivityAt: string | null;
}

export interface ListMerchantsParams {
    page?: number;
    limit?: number;
    /** Case-insensitive match on `name`. */
    search?: string;
}

export interface MerchantsPage {
    pagination: MetaPagination;
    merchants: MerchantSummary[];
}

// ─── Staff channel (#4711) ───────────────────────────────────────────────────

/**
 * One person on `people[]` we may be able to reach.
 *
 * There is no WhatsApp group to connect: the Business API sends to individual
 * numbers and has no group API, so the "staff channel" IS the people list. A
 * member counts as connected once they have messaged the merchant's number,
 * which is both the proof the number is on WhatsApp and what opens their
 * 24-hour window.
 */
export interface StaffChannelMember {
    id: string;
    name: string;
    role: string;
    phone: string;
    /** We have heard from this number at least once. */
    connected: boolean;
    /** Null means we have never heard from them. */
    lastSeenAt: string | null;
    /** A free-form alert can reach them right now. False is not unreachable —
     *  an approved template still delivers — so say which of the two it is. */
    windowOpen: boolean;
}

/** `GET .../merchants/{merchantId}/staff-channel`. */
export interface StaffChannelState {
    name: string;
    /** At least one member has messaged us. */
    connected: boolean;
    /** Members we have actually heard from, NOT the size of `people[]`. */
    memberCount: number;
    /** The `people[]` figure: invited, not yet reached. */
    invitedCount: number;
    connectedAt: string | null;
    /** `wa.me/<number>?text=[RCP:<slug>] …` — what the merchant forwards to the
     *  team. Each member who sends it flips to connected. */
    connectLink: string | null;
    members: StaffChannelMember[];
}

/**
 * `POST .../merchants/{merchantId}/staff-channel/test`.
 *
 * `not_connected` is the server's 409 (`staff_channel_not_connected`): nobody
 * has messaged in, so there is no one to send to. It carries the server's own
 * sentence, which already tells the merchant to share the link.
 */
export type SendStaffTestResult =
    | { ok: true; delivered: boolean; sentTo: unknown[]; memberCount: number }
    | { ok: false; kind: "not_connected"; message: string }
    | RequestFailure;

// ─── WhatsApp onboarding (#4671) ─────────────────────────────────────────────

/**
 * Meta's approval state for ONE template. Carries `rejected`, which the flow
 * payload's `TemplateStatus` deliberately does not: #4668 reports a rejected
 * template as `not_submitted` on `GET .../flow`, and only this endpoint says
 * why. Kept as its own union so `TemplateStatus` is never widened by accident.
 */
export type WhatsappTemplateStatus = "approved" | "pending" | "not_submitted" | "rejected";

/** The seven keys are fixed; #4668 and #4675 look a template up by them. */
export type WhatsappTemplateKey =
    | "booking_confirmed"
    | "reminder_24h"
    | "reminder_same_day"
    | "stall_nudge"
    | "post_visit_rebook"
    | "no_show_recovery"
    | "morning_digest";

export interface WhatsappTemplate {
    key: WhatsappTemplateKey;
    status: WhatsappTemplateStatus;
    metaName: string | null;
    submittedAt: string | null;
    reviewedAt: string | null;
    rejectionReason: string | null;
}

export interface WhatsappChecklist {
    detailsConfirmed: boolean;
    metaVerified: boolean;
    numberMigrated: boolean;
    templatesApproved: boolean;
    kbSignedOff: boolean;
}

/** `GET .../merchants/{merchantId}/whatsapp`. */
export interface WhatsappState {
    /** E.164. Secondary detail at most; `displayNumber` is what the card shows. */
    number: string;
    displayNumber: string;
    /** The same fact as `MerchantConfig.whatsappConnected` — render it once. */
    connected: boolean;
    checklist: WhatsappChecklist;
    onboardingStage: OnboardingStage;
    /** Null until the merchant has accepted that a number moved to the API
     *  leaves the WhatsApp Business app. Migration is refused without it. */
    oneWayDoorAcceptedAt: string | null;
    /** Who signed off the knowledge base and when (ourteam-backend#6674). Optional: an older backend does not send them. */
    kbSignedOffAt?: string | null;
    kbSignedOffBy?: string | null;
    templates: WhatsappTemplate[];
    /**
     * Shared-number mode (decision on ourteam-backend#4666, 3 September 2026).
     * While `RECEPTIONIST_SHARED_NUMBER` is on and the merchant has no migrated
     * number of their own, they run on the platform number: `metaVerified`,
     * `numberMigrated` and `connected` then report true because they belong to
     * that number, and `migrate` is not needed. Optional because an older
     * backend does not send them; absent reads as "own number".
     */
    onPlatformNumber?: boolean;
    /** `https://wa.me/<number>?text=[RCP:<slug>]` — the link the merchant
     *  shares. On the platform number the tag is what routes the customer to
     *  them. Null when the merchant has their own number. */
    entryLink?: string | null;
}

/**
 * `POST .../whatsapp/migrate` is refused with 409 `one_way_door_not_accepted`
 * until `accept-terms` has been recorded. The screen reopens the consent
 * dialog on that, so it needs to be told apart from a failure.
 */
export type MigrateResult =
    | { ok: true; state: WhatsappState }
    | { ok: false; kind: "terms_not_accepted"; message: string }
    | RequestFailure;

// ─── Learn from chats (#4672) ────────────────────────────────────────────────

/**
 * `POST .../transcripts/analyse` is synchronous when it finishes within ~30s
 * and otherwise answers 202 with a job to poll via `getAnalysisJob`.
 */
export type AnalyseResult =
    | { status: "running"; jobId: string }
    | { status: "done"; threadCount: number; proposals: Proposal[] };

/**
 * `POST .../transcripts/apply` writes ONLY the given proposals and returns both
 * updated documents so the screen re-renders without a second fetch. It goes
 * through the same validator as a flow save, so it can answer 422 with
 * `problems` — and then it has written nothing.
 */
export type ApplyProposalsResult =
    | { ok: true; merchant: MerchantConfig; flow: BookingFlow }
    | { ok: false; kind: "invalid"; problems: string[] }
    | RequestFailure;

